import { useEffect } from 'react';
import { Button } from '@/core/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/core/components/ui/sheet';
import { LanguageForm } from '../molecules/LanguageForm';
import { Language } from '../../types/translation-types';
import {
  CreateLanguageRequest,
  UpdateLanguageRequest,
} from '../../types/language-management-types';
import { useLanguageManagementStore } from '../../stores/language-management-store';
import { useSoundManagementStore } from '../../stores/sound-management-store';
import { toast } from 'sonner';

interface LanguageBladeProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language | null;
}

export function LanguageBlade({ isOpen, onClose, language }: LanguageBladeProps) {
  const {
    isSubmitting,
    submitError,
    createLanguage,
    updateLanguage,
    copyLanguageValues,
    clearSubmitError,
  } = useLanguageManagementStore();
  const { copySoundsFromParent } = useSoundManagementStore();

  const isEditMode = language !== null;
  const title = isEditMode ? 'Edit Language' : 'Add Language';

  // Clear errors when blade opens/closes
  useEffect(() => {
    if (isOpen) {
      clearSubmitError();
    }
  }, [isOpen, clearSubmitError]);

  const handleSubmit = async (
    formData: CreateLanguageRequest | UpdateLanguageRequest,
    copyFromLanguageCode?: string,
    copySoundsFromLanguageCode?: string
  ) => {
    try {
      if (isEditMode && language) {
        await updateLanguage(language.languageId, formData as UpdateLanguageRequest);
        toast.success('Language updated successfully');
      } else {
        const createData = formData as CreateLanguageRequest;
        const created = await createLanguage(createData);
        toast.success('Language created successfully');

        if (copyFromLanguageCode) {
          try {
            const result = await copyLanguageValues(
              created.languageCode,
              copyFromLanguageCode
            );
            toast.success(
              `Copied ${result.copiedCount} translations from ${copyFromLanguageCode}`
            );
          } catch {
            toast.error(
              `Language created, but copying translations from ${copyFromLanguageCode} failed — translations will need to be added manually`
            );
          }
        }

        if (copySoundsFromLanguageCode) {
          try {
            const soundsResult = await copySoundsFromParent(
              created.languageCode,
              copySoundsFromLanguageCode
            );
            toast.success(
              `Copied ${soundsResult.copiedCount} sound file(s) from ${copySoundsFromLanguageCode}`
            );
          } catch {
            toast.error(
              `Language created, but copying sound files from ${copySoundsFromLanguageCode} failed — sounds will need to be added manually`
            );
          }
        }
      }
      onClose();
    } catch {
      toast.error(isEditMode ? 'Failed to update language' : 'Failed to create language');
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-[500px] sm:w-[600px] sm:max-w-[600px] flex flex-col">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-6">
          <LanguageForm
            language={language}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            error={submitError}
            isEditMode={isEditMode}
          />
        </div>

        <SheetFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" form="language-form" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
